import React from 'react';
import { Option, Question } from '../../types';
import Select, { MultiValue } from 'react-select';
import { useLanguage } from '../../LanguageContext';
import { getTranslation } from '../../consts/translations';
import { getValidationMessage } from '../../utils/validationMessages';

interface FormSelectProps {
  question: Question;
  errors: Record<string, string>;
  setErrors: (value: Record<string, string>) => void;
  handleChange: (questionKey: string, answer: Option[]) => void;
  disabled: boolean;
}

const FormSelect: React.FC<FormSelectProps> = ({
  question,
  errors,
  setErrors,
  handleChange,
  disabled,
}) => {
  const { language } = useLanguage();
  const translation = getTranslation(language);
  const handleSelectChange = (
    question: Question,
    selectedOptions:
      MultiValue<{ value: string; label: string }> | { value: string; label: string } | null,
  ) => {
    let selectedValues: Option[] = [];

    if (selectedOptions) {
      if ('value' in selectedOptions) {
        selectedValues = [{ text: selectedOptions.label, guid: selectedOptions.value }];
      } else if (Array.isArray(selectedOptions)) {
        selectedValues = selectedOptions.map((option) => ({
          text: option.label,
          guid: option.value,
        }));
      }
    }

    const exclusiveOption = question.options?.find(
      (option) => option.exclusive && selectedValues.some((answer) => answer.guid === option.guid),
    );

    if (exclusiveOption) {
      selectedValues = [{ guid: exclusiveOption.guid, text: exclusiveOption.text }];
    }

    if (question.min_value !== undefined && selectedValues.length < question.min_value) {
      setErrors({
        ...errors,
        [question.question_key]: getValidationMessage(language, 'select_min_options', {
          min: question.min_value,
        }),
      });
    } else {
      const next = { ...errors };
      delete next[question.question_key];
      setErrors(next);
    }

    handleChange(question.question_key, selectedValues);
  };

  const isMaxReached =
    question.max_value !== undefined && (question.answer?.length ?? 0) >= question.max_value;

  const mappedExistingAnswer = question.answer?.map((answer) => ({
    value: answer.guid,
    label: String(answer.text),
  }));

  const exclusiveOption = question.options?.find((option) =>
    question.answer?.some((answer) => answer.guid === option.guid && option.exclusive),
  );

  return (
    <div>
      <Select
        id={question.question_key}
        className='question-select'
        isMulti={question.multiple_select}
        name={question.question_key}
        isClearable={true}
        options={question.options
          ?.filter((option) => option.active)
          .map((option) => ({
            value: option.guid,
            label: String(option.text),
            isDisabled:
              (exclusiveOption && option.guid !== exclusiveOption.guid) ||
              (isMaxReached && !question.answer?.some((ans) => ans.guid === option.guid)),
          }))}
        placeholder={translation.select_placeholder}
        closeMenuOnSelect={!question.multiple_select}
        hideSelectedOptions={false}
        value={mappedExistingAnswer}
        onChange={(selectedOptions) => handleSelectChange(question, selectedOptions)}
        isOptionDisabled={(option) =>
          (exclusiveOption && option.value !== exclusiveOption.guid) ||
          (isMaxReached && !question.answer?.some((ans) => ans.guid === option.value))
        }
        isDisabled={disabled}
      />
      {errors[question.question_key] && (
        <span className='error-text' style={{ color: 'red', fontSize: '12px' }}>
          {errors[question.question_key]}
        </span>
      )}
    </div>
  );
};

export default FormSelect;
